import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import DialogTitle from 'components/DialogOverrides/DialogTitle';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  colors,
  Dialog,
  DialogActions,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  compareLabourScopedBillingRatesImport,
  compareLabourScopedBillingRatesUpdateImport,
  bulkUpdateLabourScopedBillingRates,
  downloadLabourScopedBillingRatesNewTemplate,
  downloadLabourScopedBillingRatesUpdateTemplate,
  hasExistingScopedBillingRates,
  BulkUpdateScopedBillingRatesResponse,
  ImportMode,
} from 'api/labourApi';
import { Branch } from 'api/branchApi';
import { Client } from 'api/clientsApi';
import { handleMutationError, handleMutationErrorTitle } from 'util/handleMutationError';
import LabourScopeBillingRatesForm, { isBillingComboReady } from './LabourScopeBillingRatesForm';
import { ScopedBillingResultView } from './LabourImportResultView';
import { comboLabel } from '../Utils/Utils';

interface Props {
  open: boolean;
  handleClose: () => void;
}

const TAB_LABEL: Record<ImportMode, string> = {
  new: 'Import New Billing Rates by Role',
  update: 'Update Existing Billing Rates by Role',
};

const DESCRIPTION_TEXT: Record<ImportMode, string> = {
  new: "Download a template pre-filled with all 4 roles for this scope, set a first-time Billing Rate for the ones you want, then upload it back.",
  update: "Download a template pre-filled with your active roles' current billing rates for this scope, edit the values you want to change, then upload it back.",
};

const INFO_TEXT: Record<ImportMode, string> = {
  new: "The template is pre-filled with all 4 roles for the selected Branch/Client scope. The Role column is read-only for reference. Enter a value in Billing Rate and Working Away From Home Billing Rate to give the role its first billing rate at this scope, with an Effective Date (use YYYY-MM-DD format, leave blank to use today's date). Both values are required together for a role's first rate at this scope. Rows left blank are skipped. All changes are presented for review and are not saved until confirmed.",
  update: "A pre-filled list of all 4 roles for the selected Branch/Client scope is provided. The Role, Current Billing Rate, Current Working Away From Home Billing Rate, and Current Billing Rate Effective From Date columns are read-only for reference. Enter a value in New Billing Rate and/or New Working Away From Home Billing Rate to update it, with a New Billing Rate Effective From Date (use YYYY-MM-DD format, leave blank to use today's date). To set a rate for a role that has none yet at this scope, both New values are required. All changes are presented for review and are not saved until confirmed.",
};

const ScopedBillingRatesTab = ({
  mode,
  branch,
  setBranch,
  client,
  setClient,
  onBusyChange,
  onClose,
}: {
  mode: ImportMode;
  branch: Branch | null;
  setBranch: (branch: Branch | null) => void;
  client: Client | null;
  setClient: (client: Client | null) => void;
  onBusyChange: (busy: boolean) => void;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [scopeError, setScopeError] = useState<{ title: string; message: string } | null>(null);
  const [preview, setPreview] = useState<BulkUpdateScopedBillingRatesResponse | null>(null);
  const [result, setResult] = useState<BulkUpdateScopedBillingRatesResponse | null>(null);
  const comboReady = isBillingComboReady(branch, client);
  const { data: hasExistingRates } = useQuery(
    ['scoped-billing-rates-exist', branch?.id, client?.id],
    () => hasExistingScopedBillingRates({ branchId: branch?.id, clientId: client?.id }),
    { enabled: mode === 'update' && comboReady }
  );

  const { mutate: previewMutate, isLoading: isPreviewLoading } = useMutation(
    mode === 'update' ? compareLabourScopedBillingRatesUpdateImport : compareLabourScopedBillingRatesImport,
    {
      onSuccess: (data) => setPreview(data),
      onError: (error) => {
        const title = handleMutationErrorTitle(error);
        if (title) {
          setScopeError({ title, message: handleMutationError(error) });
          return;
        }
        enqueueSnackbar(`Failed to read the file: ${handleMutationError(error)}`, { variant: 'error' });
      },
    }
  );

  const { mutate: commitMutate, isLoading: isCommitLoading } = useMutation(
    bulkUpdateLabourScopedBillingRates,
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(['labour-rates']);
        setResult(data);
        enqueueSnackbar(
          data.errorCount === 0
            ? `Billing rates updated for ${comboLabel(branch, client)}`
            : `Billing rates updated for ${comboLabel(branch, client)} — ${data.errorCount} row(s) skipped`,
          { variant: 'success' }
        );
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to update billing rates: ${handleMutationError(error)}`, {
          variant: 'error',
        });
      },
    }
  );

  const { mutate: downloadMutate, isLoading: isDownloadingTemplate } = useMutation(
    mode === 'update' ? downloadLabourScopedBillingRatesUpdateTemplate : downloadLabourScopedBillingRatesNewTemplate,
    {
      onError: (error) => {
        enqueueSnackbar(`Failed to download the template: ${handleMutationError(error)}`, {
          variant: 'error',
        });
      },
    }
  );

  const isBusy = isPreviewLoading || isCommitLoading || isDownloadingTemplate;
  React.useEffect(() => onBusyChange(isBusy), [isBusy, onBusyChange]);

  const step = result ? 'result' : preview ? 'preview' : 'form';
  const label = comboLabel(branch, client);

  const noExistingScopedData = mode === 'update' && comboReady && hasExistingRates === false;

  const resultDivergedFromPreview =
    !!preview &&
    !!result &&
    (preview.createdCount !== result.createdCount ||
      preview.updatedCount !== result.updatedCount ||
      preview.ignoredCount !== result.ignoredCount ||
      preview.errorCount !== result.errorCount);

  return (
    <>
      {step === 'form' && (
        <>
          <LabourScopeBillingRatesForm
            mode={mode}
            file={file}
            setFile={setFile}
            isLoading={isPreviewLoading}
            descriptionText={DESCRIPTION_TEXT[mode]}
            infoText={INFO_TEXT[mode]}
            branch={branch}
            setBranch={(next) => {
              setScopeError(null);
              setBranch(next);
            }}
            client={client}
            setClient={(next) => {
              setScopeError(null);
              setClient(next);
            }}
            scopeError={scopeError}
            noExistingDataMessage={
              noExistingScopedData
                ? `No existing billing rates yet for ${label}. Use the "Import New Billing Rates by Role" tab to add some first.`
                : null
            }
            onDownloadTemplate={() => {
              if (!comboReady) return;
              downloadMutate({
                branchId: branch?.id,
                clientId: client?.id,
                branchName: branch?.name,
                clientName: client?.legalName,
              });
            }}
            isDownloadingTemplate={isDownloadingTemplate}
          />
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button onClick={onClose} sx={{ color: colors.grey[600], mr: 1 }} disabled={isPreviewLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isPreviewLoading || !file || !comboReady}
                endIcon={isPreviewLoading ? <CircularProgress size={15} /> : <ArrowForwardIcon />}
                onClick={() => {
                  if (!file || !comboReady) return;
                  setScopeError(null);
                  previewMutate({ file, branchId: branch?.id, clientId: client?.id });
                }}>
                {isPreviewLoading ? 'Please wait...' : 'Preview'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'preview' && preview && (
        <>
          <Typography variant="h6" sx={{ m: 2, mb: 0 }}>
            {mode === 'update' ? 'Updating existing rates' : 'Importing new rates'} for {label}
          </Typography>
          <ScopedBillingResultView response={preview} />
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button onClick={() => setPreview(null)} sx={{ color: colors.grey[600], mr: 1 }} disabled={isCommitLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  isCommitLoading ||
                  !preview.previewToken ||
                  (preview.createdCount === 0 && preview.updatedCount === 0)
                }
                endIcon={isCommitLoading ? <CircularProgress size={15} /> : <CheckIcon />}
                onClick={() => {
                  if (!preview.previewToken) return;
                  commitMutate({ previewToken: preview.previewToken });
                }}>
                {isCommitLoading
                  ? 'Please wait...'
                  : preview.errorCount > 0
                    ? `Ignore Errors and Confirm Import for ${label}`
                    : `Confirm Import for ${label}`}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'result' && result && (
        <>
          <Typography variant="h6" sx={{ m: 2, mb: 0 }}>
            {mode === 'update' ? 'Updated existing rates' : 'Imported new rates'} for {label}
          </Typography>
          {resultDivergedFromPreview && (
            <Alert severity="warning" sx={{ m: 2, mt: 0 }}>
              The committed result differs from the preview you approved, data may have changed since
              preview. Review the details below.
            </Alert>
          )}
          <ScopedBillingResultView response={result} />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button variant="contained" color="primary" endIcon={<CheckIcon />} onClick={onClose}>
                Done
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </>
  );
};

const BulkUpdateScopedBillingRatesDialog = ({ open, handleClose }: Props) => {
  const [isBusy, setIsBusy] = useState(false);
  const [mode, setMode] = useState<ImportMode>('new');
  const [branch, setBranch] = useState<Branch | null>(null);
  const [client, setClient] = useState<Client | null>(null);

  const onClose = () => {
    if (isBusy) return;
    handleClose();
  };

  return (
    <Dialog open={open} onClose={onClose} disableEnforceFocus fullWidth scroll="paper" maxWidth="lg">
      <DialogTitle onClose={onClose}>Import Branch/Client Specific Billing Rates by Role</DialogTitle>
      <Divider />
      <Tabs
        value={mode}
        onChange={(_e, value) => {
          if (isBusy) return;
          setMode(value);
        }}
        variant="fullWidth">
        <Tab label={TAB_LABEL.new} value="new" disabled={isBusy} />
        <Tab label={TAB_LABEL.update} value="update" disabled={isBusy} />
      </Tabs>
      <Divider />

      <ScopedBillingRatesTab
        key={mode}
        mode={mode}
        branch={branch}
        setBranch={setBranch}
        client={client}
        setClient={setClient}
        onBusyChange={setIsBusy}
        onClose={onClose}
      />
    </Dialog>
  );
};

export default BulkUpdateScopedBillingRatesDialog;
